import React from 'react'
import { ArrowDownCircle, ArrowUpCircle, CheckCheck, XCircle } from 'lucide-react'
import Button from '../../../../../components/ui/button'
import clsx from 'clsx'

export type ScriptSuggestionsBarProps = {
  metadata: {
    message: string
    summary?: string | null
    linesChanged?: number | null
    generatedAt: string
    instruction: string
  } | null
  totalSuggestions: number
  activeIndex: number
  onAcceptAll: () => void
  onRejectAll: () => void
  onPrev: () => void
  onNext: () => void
  isApplyingAll: boolean
  actionsLocked?: boolean
}

export const ScriptSuggestionsBar: React.FC<ScriptSuggestionsBarProps> = ({
  metadata,
  totalSuggestions,
  activeIndex,
  onAcceptAll,
  onRejectAll,
  onPrev,
  onNext,
  isApplyingAll,
  actionsLocked = false,
}) => {
  if (!metadata) return null

  const activeLabel = totalSuggestions > 0 ? `${activeIndex + 1} of ${totalSuggestions}` : 'No pending suggestions'
  const rawSummary = metadata.summary || metadata.message

  // Shorten overly verbose summaries to a crisp, scannable line
  const formatSummary = (input: string): string => {
    let s = input.trim()
    const MAX = 160
    if (s.length > MAX) {
      const cut = s.slice(0, MAX)
      const lastSpace = cut.lastIndexOf(' ')
      s = (lastSpace > 40 ? cut.slice(0, lastSpace) : cut).trimEnd() + '…'
    }
    return s
  }

  const summary = formatSummary(rawSummary)

  return (
    <div className="sticky top-2 z-20 mb-5 rounded-2xl bg-white px-4 py-3 shadow-sm ring-1 ring-black/5">
      <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3">
        {/* Left: vertical prev/next controls */}
        <div className="flex flex-col items-center justify-center gap-1">
          <button
            type="button"
            aria-label="Previous suggestion"
            title="Previous suggestion"
            className={clsx(
              'cursor-pointer inline-flex h-8 w-8 items-center justify-center rounded-md border border-black/10 text-foreground/70 hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
              (totalSuggestions === 0 || actionsLocked || isApplyingAll) && 'cursor-not-allowed opacity-50'
            )}
            onClick={onPrev}
            disabled={totalSuggestions === 0 || actionsLocked || isApplyingAll}
          >
            <ArrowUpCircle className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Next suggestion"
            title="Next suggestion"
            className={clsx(
              'cursor-pointer inline-flex h-8 w-8 items-center justify-center rounded-md border border-black/10 text-foreground/70 hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
              (totalSuggestions === 0 || actionsLocked || isApplyingAll) && 'cursor-not-allowed opacity-50'
            )}
            onClick={onNext}
            disabled={totalSuggestions === 0 || actionsLocked || isApplyingAll}
          >
            <ArrowDownCircle className="h-4 w-4" />
          </button>
        </div>
        {/* Middle: summary */}
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <ArrowDownCircle className="h-4 w-4 text-brand-600" />
            <span>Updates ready to review</span>
          </div>
          <p className="max-w-3xl text-sm text-foreground/70">{summary}</p>
          <div className="text-xs text-foreground/70">{activeLabel}</div>
        </div>
        {/* Right: bulk actions */}
        <div className="flex flex-col items-stretch justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onRejectAll}
            disabled={totalSuggestions === 0 || actionsLocked || isApplyingAll}
            className="cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
            aria-label="Dismiss all suggestions"
          >
            <XCircle className="h-4 w-4" />
            <span className="hidden sm:inline">Dismiss all</span>
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={onAcceptAll}
            disabled={totalSuggestions === 0 || actionsLocked || isApplyingAll}
            className="cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
            aria-label={isApplyingAll ? 'Applying all suggestions' : 'Accept all suggestions'}
          >
            <CheckCheck className="h-4 w-4" />
            <span className="hidden sm:inline">{isApplyingAll ? 'Applying…' : 'Accept all'}</span>
          </Button>
        </div>
      </div>
    </div>
  )
}
